import { useEffect, useState, useCallback } from "react";
import { useAuth, api } from "../App";
import { useLocation, Link, useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Input } from "../components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import Logo from "../components/Logo";
import StoreSelector from "../components/StoreSelector";
import GettingStartedChecklist from "../components/GettingStartedChecklist";
import { toast } from "sonner";
import { trackStoreConnected, trackSubscription } from "../utils/analytics";
import { 
  TrendingDown,
  TrendingUp, 
  Store, 
  RefreshCw, 
  DollarSign, 
  ShoppingCart, 
  Users, 
  Package, 
  AlertTriangle,
  Clock,
  Settings,
  LogOut,
  ExternalLink,
  Loader2,
  CheckCircle2,
  XCircle,
  Info,
  CreditCard,
  Zap,
  Lock,
  AlertCircle,
  Play,
  Unplug,
  Plus,
  Shield,
  Gift,
  Bell,
  Building2
} from "lucide-react";

// Admin emails list
const ADMIN_EMAILS = ["reynir@replydriven.com"];

// Helper to check if user is an admin
const isAdminUser = (user) => {
  return !!user?.email && ADMIN_EMAILS.includes(user.email.toLowerCase());
};

const shouldShowSetupInstructions = (user) => {
  return process.env.REACT_APP_SHOW_SETUP_INSTRUCTIONS === "true" || isAdminUser(user);
};

const Dashboard = () => {
  const { user, setUser, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [stores, setStores] = useState([]);  // Multiple stores support
  const [store, setStore] = useState(null);  // Legacy: first/primary store
  const [activeStoreId, setActiveStoreId] = useState(null);  // Currently selected store
  const [latestReport, setLatestReport] = useState(null);
  const [reportHistory, setReportHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [shopDomain, setShopDomain] = useState("");
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [healthStatus, setHealthStatus] = useState(null);
  const [billingStatus, setBillingStatus] = useState(null);
  const [creatingCheckout, setCreatingCheckout] = useState(false);
  const [creatingPortal, setCreatingPortal] = useState(false);
  const [showAddStore, setShowAddStore] = useState(false);  // For adding additional stores
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);  // For plan upgrade modal
  const [portalRedirectAttempted, setPortalRedirectAttempted] = useState(false);  // Prevent loops
  const [storeLimit, setStoreLimit] = useState(1);  // Max stores for current plan
  const [adminBadgeCount, setAdminBadgeCount] = useState(0);  // Admin nav badge
  const [payoutRequestsCount, setPayoutRequestsCount] = useState(0);  // For deep-link to payouts
  const [backendError, setBackendError] = useState(null);  // Track backend/network errors
  const [isAffiliate, setIsAffiliate] = useState(false);  // Affiliate status for nav
  const [showBillingPending, setShowBillingPending] = useState(false);  // Billing pending modal
  const [billingEnabled, setBillingEnabled] = useState(true);  // Billing enabled flag
  const [notifyRegistered, setNotifyRegistered] = useState(false);  // User registered for billing notify
  const [registeringIntent, setRegisteringIntent] = useState(false);  // Loading state for notify
  const [oauthError, setOauthError] = useState(null);  // Shopify OAuth error state
  const [affiliateCtaDismissed, setAffiliateCtaDismissed] = useState(false);  // Affiliate CTA dismissed
  const [showFirstInsightAffiliate, setShowFirstInsightAffiliate] = useState(false);  // First insight affiliate modal

  const currentUser = location.state?.user || user;
  const showSetupInstructions = shouldShowSetupInstructions(currentUser);
  const isAdmin = isAdminUser(currentUser);

  // Check if user just connected a store via Shopify OAuth and needs to go to preview
  useEffect(() => {
    const justConnectedStore = localStorage.getItem('just_connected_store');
    const justConnectedTime = localStorage.getItem('just_connected_store_time');
    
    if (justConnectedStore && justConnectedTime) {
      const elapsed = Date.now() - parseInt(justConnectedTime, 10);
      // Only redirect if within 5 minutes (300000ms)
      if (elapsed < 300000) {
        // User just connected a store but ended up at Dashboard instead of Onboarding
        // This can happen if session was lost during OAuth redirect
        console.log("User just connected store, redirecting to onboarding...", justConnectedStore);
        localStorage.removeItem('just_connected_store');
        localStorage.removeItem('just_connected_store_time');
        navigate(`/onboarding?store_connected=true&store_id=${justConnectedStore}`, { replace: true });
      } else {
        // Expired, clean up
        localStorage.removeItem('just_connected_store');
        localStorage.removeItem('just_connected_store_time');
      }
    }
  }, [navigate]);

  // Poll for admin badge counts (every 60s)
  useEffect(() => {
    if (!isAdmin) return;
    
    const fetchBadgeCounts = async () => {
      try {
        const res = await api.get("/admin/badge-counts");
        setAdminBadgeCount(res.data?.total || 0);
        setPayoutRequestsCount(res.data?.payout_requests || 0);
      } catch (err) {
        // Silently fail - badge is non-critical
      }
    };
    
    fetchBadgeCounts();
    const interval = setInterval(fetchBadgeCounts, 60000);
    return () => clearInterval(interval);
  }, [isAdmin]);

  // Load affiliate CTA dismissed state from localStorage
  useEffect(() => {
    const dismissed = localStorage.getItem('affiliateCtaDismissed');
    if (dismissed === 'true') {
      setAffiliateCtaDismissed(true);
    }
  }, []);

  // Show first insight affiliate CTA (high intent moment)
  useEffect(() => {
    // Only show if: has report, not already affiliate, not dismissed, and hasn't seen this CTA
    const hasSeenFirstInsightCta = localStorage.getItem('hasSeenFirstInsightCta');
    if (latestReport && !isAffiliate && !affiliateCtaDismissed && !hasSeenFirstInsightCta) {
      // Small delay to let user see the insight first
      const timer = setTimeout(() => {
        setShowFirstInsightAffiliate(true);
        localStorage.setItem('hasSeenFirstInsightCta', 'true');
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [latestReport, isAffiliate, affiliateCtaDismissed]);

  // Load active store from localStorage or API
  useEffect(() => {
    const loadActiveStore = async () => {
      // First check localStorage for cached selection
      const cachedStoreId = localStorage.getItem('activeStoreId');
      if (cachedStoreId) {
        setActiveStoreId(cachedStoreId);
      }
      
      // Then verify with backend
      try {
        const res = await api.get("/user/active-store");
        if (res.data?.active_store_id) {
          setActiveStoreId(res.data.active_store_id);
          localStorage.setItem('activeStoreId', res.data.active_store_id);
        }
      } catch (error) {
        console.error("Failed to load active store:", error);
      }
    };
    
    loadActiveStore();
  }, []);

  // Handle store selection change
  const handleStoreChange = async (newStoreId) => {
    if (newStoreId === activeStoreId) return;
    
    console.log(`Switching to store: ${newStoreId}`);
    setActiveStoreId(newStoreId);
    localStorage.setItem('activeStoreId', newStoreId);
    
    // Update backend
    try {
      await api.put("/user/active-store", { store_id: newStoreId });
    } catch (error) {
      console.error("Failed to persist active store:", error);
    }
    
    // Refetch data for new store
    fetchStoreData(newStoreId);
    toast.success("Switched store");
  };

  // Fetch data for a specific store
  const fetchStoreData = async (storeId) => {
    if (!storeId) return;
    
    try {
      // Fetch latest analysis and history for specific store
      const [latestRes, historyRes] = await Promise.all([
        api.get(`/analysis/latest?store_id=${storeId}`).catch(() => ({ data: null })),
        api.get(`/analysis/history?store_id=${storeId}`).catch(() => ({ data: [] }))
      ]);
      
      setLatestReport(latestRes.data);
      setReportHistory(Array.isArray(historyRes.data) ? historyRes.data : []);
    } catch (error) {
      console.error("Failed to fetch store data:", error);
    }
  };

  // Handle manage_subscription=true query param - auto-redirect to billing portal
  useEffect(() => {
    const manageSubscription = searchParams.get('manage_subscription');
    
    if (manageSubscription === 'true' && !portalRedirectAttempted && !creatingPortal) {
      // Remove the query param immediately to prevent loops
      setSearchParams(prev => {
        const newParams = new URLSearchParams(prev);
        newParams.delete('manage_subscription');
        return newParams;
      }, { replace: true });
      
      setPortalRedirectAttempted(true);
      
      // Auto-open billing portal
      (async () => {
        setCreatingPortal(true);
        try {
          console.log("Auto-opening billing portal due to manage_subscription param");
          const res = await api.post("/billing/create-portal", {});
          if (res.data.url && !res.data.fallback) {
            console.log("✅ Redirecting to billing portal:", res.data.url.substring(0, 50) + "...");
            window.location.href = res.data.url;
          } else {
            toast.error("Unable to open billing portal. Please try again.");
          }
        } catch (error) {
          const detail = error.response?.data?.detail;
          if (typeof detail === 'object' && detail.action === 'subscribe') {
            toast.info("No active subscription. Please subscribe first.");
          } else {
            toast.error("Unable to open billing portal. Please contact support.");
          }
          console.error("Portal redirect error:", error);
        } finally {
          setCreatingPortal(false);
        }
      })();
    }
  }, [searchParams, setSearchParams, portalRedirectAttempted, creatingPortal]);

  // Handle checkout success/cancel from Shopify billing redirect
  useEffect(() => {
    const checkoutStatus = searchParams.get('checkout');
    const paymentStatus = searchParams.get('payment');
    const sessionId = searchParams.get('session_id');
    const errorParam = searchParams.get('error');
    
    // PHASE 7: Improved error messaging with clear, specific messages
    if (errorParam) {
      const errorMessages = {
        'invalid_state': {
          title: 'Connection expired',
          message: 'Your connection session expired. Please try connecting again.',
          recoverable: true
        },
        'safari_private_mode': {
          title: 'Safari Private Browsing Detected',
          message: 'Safari private browsing may block authentication. Please retry in normal Safari mode or use Chrome.',
          recoverable: true,
          isSafari: true
        },
        'state_expired': {
          title: 'Session expired',
          message: 'Your Shopify connection session expired. Please try again.',
          recoverable: true
        },
        'missing_params': {
          title: 'Connection incomplete',
          message: 'The Shopify connection was interrupted. Please try again.',
          recoverable: true
        },
        'token_exchange_failed': {
          title: 'Shopify connection failed',
          message: 'We couldn\'t complete the connection with Shopify. Please try again.',
          recoverable: true
        },
        'shopify_not_configured': {
          title: 'Shopify not configured',
          message: 'Shopify integration is not available. Please contact support.',
          recoverable: false
        },
        'store_already_connected': {
          title: 'Store already connected',
          message: 'This store is already connected to another account.',
          recoverable: false
        },
        'store_limit_reached': {
          title: 'Store limit reached',
          message: 'Your plan allows a limited number of stores. Upgrade required.',
          recoverable: false,
          upgradeRequired: true
        },
        'payment_required': {
          title: 'Payment required',
          message: 'Payment required to unlock full analysis.',
          recoverable: true
        }
      };
      
      const errorInfo = errorMessages[errorParam] || {
        title: 'Connection error',
        message: 'Something went wrong. Please try again.',
        recoverable: true
      };
      
      // Log OAuth debug info for troubleshooting
      const oauthDebugInfo = localStorage.getItem('oauth_debug_state');
      console.error('[Shopify OAuth] Error:', errorParam, {
        errorInfo,
        savedDebugState: oauthDebugInfo ? JSON.parse(oauthDebugInfo) : null,
        url: window.location.href,
        userAgent: navigator.userAgent
      });
      localStorage.removeItem('oauth_debug_state');
      
      setOauthError(errorInfo);
      
      // Clear the error param from URL
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('error');
      setSearchParams(newParams, { replace: true });
    }
    
    // Handle Shopify billing redirect (?billing=success)
    if (checkoutStatus === 'success' || searchParams.get('billing') === 'success') {
      handleCheckoutSuccess();
    } else if (checkoutStatus === 'cancelled' || searchParams.get('billing') === 'declined') {
      searchParams.delete('checkout');
      searchParams.delete('billing');
      setSearchParams(searchParams, { replace: true });
      toast.info("Subscription not activated. You can subscribe anytime from your Shopify Admin.");
    }
  }, [searchParams, setSearchParams]);

  // Handle post-checkout: poll until subscription is active
  const handleCheckoutSuccess = async () => {
    // Remove query param to prevent re-processing
    searchParams.delete('checkout');
    setSearchParams(searchParams, { replace: true });
    
    toast.loading("Setting up your account...", { id: "checkout-loading" });
    
    // Poll billing status until subscription is active (webhook may take a moment)
    let attempts = 0;
    const maxAttempts = 20; // 20 attempts x 1.5s = 30 seconds max
    
    const checkSubscription = async () => {
      try {
        const billingRes = await api.get("/billing/status");
        const status = billingRes.data?.status;
        
        if (status === 'active' || status === 'trialing') {
          toast.dismiss("checkout-loading");
          toast.success(`Welcome to ReplyDriven ${billingRes.data?.plan_name || ''}! Your subscription is active.`);
          setBillingStatus(billingRes.data);
          // Track subscription conversion in GA4
          trackSubscription(billingRes.data?.plan || 'unknown');
          // Refresh user data
          const userRes = await api.get("/auth/me");
          setUser(userRes.data);
          return;
        }
        
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(checkSubscription, 1500);
        } else {
          toast.dismiss("checkout-loading");
          toast.info("Your payment is being processed. Please refresh in a moment.");
          fetchBillingStatus();
        }
      } catch (error) {
        console.error("Error checking subscription:", error);
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(checkSubscription, 1500);
        } else {
          toast.dismiss("checkout-loading");
          toast.error("Could not verify subscription. Please contact support if this persists.");
        }
      }
    };
    
    checkSubscription();
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 10;
    
    if (attempts >= maxAttempts) {
      toast.info("Payment is being processed. Please refresh in a moment.");
      fetchBillingStatus();
      return;
    }
    
    try {
      const res = await api.get(`/billing/checkout-status/${sessionId}`);
      if (res.data.payment_status === 'paid') {
        toast.success("Payment successful! Welcome to ReplyDriven.");
        // Refresh user data and billing status
        const userRes = await api.get("/auth/me");
        setUser(userRes.data);
        fetchBillingStatus();
        // Clear URL params
        navigate('/dashboard', { replace: true });
      } else {
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
      }
    } catch (error) {
      console.error("Payment status check failed:", error);
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    }
  };

  useEffect(() => {
    if (location.state?.user && !user) {
      setUser(location.state.user);
    }
  }, [location.state, user, setUser]);

  useEffect(() => {
    fetchData();
    fetchHealth();
    fetchBillingStatus();
    checkAffiliateStatus();
  }, []);

  // Check affiliate status
  const checkAffiliateStatus = async () => {
    try {
      const res = await api.get("/affiliates/me");
      console.log("Affiliate status response:", res.data);
      setIsAffiliate(res.data?.is_affiliate === true);
    } catch (err) {
      console.error("Affiliate check failed:", err);
      setIsAffiliate(false);
    }
  };

  // Refetch data when activeStoreId changes
  useEffect(() => {
    if (activeStoreId && stores.length > 0) {
      fetchStoreData(activeStoreId);
    }
  }, [activeStoreId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [storesRes, billingRes] = await Promise.all([
        api.get("/shopify/stores").catch(() => ({ data: { stores: [], count: 0, store_limit: 1 } })),
        api.get("/billing/status").catch(() => ({ data: null }))
      ]);
      
      const fetchedStores = storesRes.data?.stores || [];
      const billingData = billingRes.data;
      
      // Check if user should be redirected to onboarding
      // Redirect if: no stores AND no active subscription (not trialing/active/lifetime)
      const status = billingData?.status;
      const hasActiveSubscription = status === "active" || status === "trialing" || status === "lifetime";
      
      if (fetchedStores.length === 0 && !hasActiveSubscription) {
        // User has no stores and no subscription - redirect to onboarding
        navigate("/onboarding", { replace: true });
        return;
      }
      
      // Track first store connection (onboarding conversion)
      const prevStoreCount = stores.length;
      if (prevStoreCount === 0 && fetchedStores.length > 0) {
        trackStoreConnected();
      }
      
      setStores(fetchedStores);
      setStoreLimit(storesRes.data?.store_limit || 1);
      setBillingStatus(billingData);
      // Also update billing enabled flag from fetchData response
      setBillingEnabled(billingData?.billing_enabled !== false);
      setNotifyRegistered(billingData?.notify_on_billing === true);
      
      // Set active store if not already set
      if (fetchedStores.length > 0 && !activeStoreId) {
        const cachedStoreId = localStorage.getItem('activeStoreId');
        const validCachedStore = fetchedStores.find(s => s.store_id === cachedStoreId);
        
        if (validCachedStore) {
          setActiveStoreId(cachedStoreId);
        } else {
          // Default to first store
          setActiveStoreId(fetchedStores[0].store_id);
          localStorage.setItem('activeStoreId', fetchedStores[0].store_id);
        }
      }
      
      // Set legacy store for backward compatibility
      const activeStore = fetchedStores.find(s => s.store_id === activeStoreId) || fetchedStores[0];
      setStore(activeStore || null);
      
      // Fetch reports for active store
      if (activeStoreId || fetchedStores[0]?.store_id) {
        const storeIdToFetch = activeStoreId || fetchedStores[0]?.store_id;
        const [reportRes, historyRes] = await Promise.all([
          api.get(`/analysis/latest?store_id=${storeIdToFetch}`).catch(() => ({ data: null })),
          api.get(`/analysis/history?store_id=${storeIdToFetch}`).catch(() => ({ data: [] }))
        ]);
        
        setLatestReport(reportRes.data);
        setReportHistory(Array.isArray(historyRes.data) ? historyRes.data : []);
      } else {
        setLatestReport(null);
        setReportHistory([]);
      }
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchHealth = async () => {
    try {
      const res = await api.get("/health");
      setHealthStatus(res.data);
    } catch (error) {
      console.error("Health check failed:", error);
    }
  };

  const fetchBillingStatus = async () => {
    try {
      const res = await api.get("/billing/status");
      setBillingStatus(res.data);
      // Update billing enabled flag
      setBillingEnabled(res.data?.billing_enabled !== false);
      setNotifyRegistered(res.data?.notify_on_billing === true);
      setBackendError(null);  // Clear any previous errors
    } catch (error) {
      console.error("Billing status check failed:", error);
      const status = error.response?.status;
      // Only set backend error for network/server errors (5xx, timeout, network failure)
      // Don't set error for 401/403 (auth issues) or 404 (user not found - real responses)
      if (!status || status >= 500 || error.code === 'ECONNABORTED' || error.message === 'Network Error') {
        setBackendError({
          type: 'billing',
          message: 'Backend temporarily unavailable',
          status: status || 'network'
        });
      }
    }
  };

  const retryBackend = async () => {
    setBackendError(null);
    setLoading(true);
    await Promise.all([
      fetchData(),
      fetchHealth(),
      fetchBillingStatus()
    ]);
    setLoading(false);
  };

  const handleConnectStore = async () => {
    if (!shopDomain.trim()) {
      toast.error("Please enter your store domain");
      return;
    }

    if (!healthStatus?.shopify_configured) {
      toast.error("Store connection is temporarily unavailable. Please try again later.");
      return;
    }

    setConnecting(true);
    try {
      const res = await api.get(`/shopify/connect?shop=${encodeURIComponent(shopDomain)}`);
      if (res.data.oauth_url) {
        window.location.href = res.data.oauth_url;
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      // Check if it's a store limit error
      if (detail && typeof detail === "object" && detail.error === "store_limit_reached") {
        toast.error(detail.message, {
          description: "Upgrade your plan to connect more stores.",
          duration: 5000
        });
      } else {
        const message = typeof detail === "string" ? detail : "Failed to connect store";
        toast.error(message);
      }
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnectStore = async (storeId = null) => {
    setDisconnecting(true);
    try {
      // Disconnect specific store by ID, or all stores if no ID provided
      if (storeId) {
        await api.delete(`/shopify/store/${storeId}`);
      } else {
        await api.delete("/shopify/store");
      }
      
      // Refresh stores list
      const storesRes = await api.get("/shopify/stores").catch(() => ({ data: { stores: [] } }));
      const newStores = storesRes.data?.stores || [];
      setStores(newStores);
      setStore(newStores.length > 0 ? newStores[0] : null);
      
      // Clear reports if no stores left
      if (newStores.length === 0) {
        setLatestReport(null);
        setReportHistory([]);
      }
      
      // Refresh billing status to get updated store count
      const billingRes = await api.get("/billing/status");
      setBillingStatus(billingRes.data);
      toast.success("Store disconnected successfully. You can reconnect anytime.");
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to disconnect store";
      toast.error(message);
    } finally {
      setDisconnecting(false);
    }
  };

  const handleRunAnalysis = async () => {
    setAnalyzing(true);
    try {
      await api.post("/analysis/run");
      toast.success("Analysis started! This may take a minute...");
      setTimeout(() => fetchData(), 30000);
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to start analysis";
      toast.error(message);
    } finally {
      setAnalyzing(false);
    }
  };

  const handleResetBilling = async () => {
    try {
      await api.post("/billing/reset");
      toast.success("Billing reset. You can now start a new subscription.");
      // Refresh billing status
      const billingRes = await api.get("/billing/status");
      setBillingStatus(billingRes.data);
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(detail || "Failed to reset billing. Please contact support.");
    }
  };

  const handleStartCheckout = async (plan = "operator", billingInterval = null) => {
    // BILLING PENDING CHECK: If billing is disabled, show modal immediately
    if (!billingEnabled) {
      setShowBillingPending(true);
      return;
    }
    
    setCreatingCheckout(true);
    
    console.log(`🛒 Starting Shopify billing for plan: ${plan}`);
    
    try {
      // Use Shopify Billing API
      const res = await api.post("/billing/shopify/create-subscription", { plan });
      console.log("📦 Backend response:", JSON.stringify(res.data, null, 2));
      
      // Double-check: if billing is pending (safety net)
      if (res.data.billing_pending || res.data.billing_enabled === false) {
        setBillingEnabled(false);
        setShowBillingPending(true);
        setCreatingCheckout(false);
        return;
      }
      
      // Redirect to Shopify confirmation URL
      if (res.data.confirmation_url) {
        console.log(`🔗 Redirecting to Shopify billing: ${res.data.confirmation_url}`);
        // Clear checkout preferences
        sessionStorage.removeItem('selected_plan');
        sessionStorage.removeItem('billing_interval');
        window.location.href = res.data.confirmation_url;
      } else {
        // No confirmation_url - billing might not be configured
        console.error("No confirmation_url in response:", res.data);
        toast.error("Failed to create subscription. Please try again.");
        setCreatingCheckout(false);
      }
    } catch (error) {
      console.error("Checkout error:", error);
      const errorMessage = error.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(errorMessage);
      setCreatingCheckout(false);
    }
  };

  const handleOpenPortal = async () => {
    setCreatingPortal(true);
    try {
      console.log("Opening billing portal...");
      const res = await api.post("/billing/create-portal", {});
      
      if (res.data.url) {
        console.log("✅ Portal URL received:", res.data.url.substring(0, 50) + "...");
        window.location.href = res.data.url;
      } else {
        toast.error("Unable to get billing portal URL. Please try again.");
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      
      if (typeof detail === 'object') {
        // Structured error response
        if (detail.action === 'subscribe') {
          toast.info("No active subscription found. Please subscribe first.");
        } else if (detail.action === 'retry') {
          toast.error(detail.message || "Billing portal unavailable. Please try again later.");
        } else {
          toast.error(detail.message || "Failed to open billing portal.");
        }
      } else if (typeof detail === 'string') {
        toast.error(detail);
      } else {
        toast.error("Failed to open billing portal. Please try again.");
      }
      console.error("Portal error:", error);
    } finally {
      setCreatingPortal(false);
    }
  };

  const handleUpgrade = async (plan) => {
    // BILLING PENDING CHECK: If billing is disabled, show modal immediately
    if (!billingEnabled) {
      setShowUpgradeModal(false);
      setShowBillingPending(true);
      return;
    }
    
    // For users with active subscription, redirect to Shopify admin
    // For users without subscription, create new subscription
    setCreatingCheckout(true);
    setShowUpgradeModal(false);
    
    const hasActiveSubscription = billingStatus?.status === 'active' || billingStatus?.status === 'trialing';
    
    if (hasActiveSubscription) {
      // User has subscription - direct them to Shopify Admin to manage
      console.log(`Directing user to Shopify Admin to manage subscription`);
      toast.info("Please manage your subscription in your Shopify Admin settings.", { duration: 5000 });
      setCreatingCheckout(false);
      return;
    }
    
    // User has no subscription - create Shopify subscription
    console.log(`Creating Shopify subscription for ${plan} plan`);
    try {
      const res = await api.post("/billing/shopify/create-subscription", { plan });
      
      // Double-check: if billing is pending (safety net)
      if (res.data.billing_pending || res.data.billing_enabled === false) {
        setBillingEnabled(false);
        setShowBillingPending(true);
        setCreatingCheckout(false);
        return;
      }
      
      // Redirect to Shopify confirmation URL
      if (res.data.confirmation_url) {
        console.log(`🔗 Redirecting to Shopify billing: ${res.data.confirmation_url}`);
        window.location.href = res.data.confirmation_url;
      } else {
        toast.error("Failed to create subscription. Please try again.");
        setCreatingCheckout(false);
      }
    } catch (error) {
      console.error("Upgrade error:", error);
      const errorMessage = error.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(errorMessage);
      setCreatingCheckout(false);
    }
  };

  // Handle billing notify intent registration
  const handleNotifyMe = async () => {
    setRegisteringIntent(true);
    try {
      await api.post("/billing/notify-intent", { store_id: activeStoreId });
      setNotifyRegistered(true);
      toast.success("You're on the list!");
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setRegisteringIntent(false);
    }
  };

  // Dismiss affiliate CTA
  const dismissAffiliateCta = () => {
    setAffiliateCtaDismissed(true);
    setShowFirstInsightAffiliate(false);
    localStorage.setItem('affiliateCtaDismissed', 'true');
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Get subscription status from billing status API (single source of truth)
  const subscriptionStatus = billingStatus?.status || "none";
  const hasActiveSubscription = subscriptionStatus === "active" || subscriptionStatus === "trialing" || subscriptionStatus === "lifetime";
  const isTrialing = subscriptionStatus === "trialing";
  const isLifetime = subscriptionStatus === "lifetime" || billingStatus?.lifetime_access;
  const trialDaysRemaining = billingStatus?.trial_days_remaining;
  const priceMonthly = billingStatus?.price_monthly || 49;
  
  // Plan info for store limits
  const currentPlan = billingStatus?.plan || "operator";
  const planName = billingStatus?.plan_name || "Operator";
  const connectedStoreCount = billingStatus?.connected_store_count || 0;
  const canAddStore = billingStatus?.can_add_store !== false;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#D4F34A] animate-spin" />
      </div>
    );
  }

  // Render paywall based on subscription status
  const renderPaywall = () => {
    // Status: none - First time user, never subscribed
    if (subscriptionStatus === "none") {
      return (
        <div className="max-w-4xl text-center">
          <div className="w-20 h-20 rounded-full bg-[#D4F34A]/10 flex items-center justify-center mx-auto mb-6">
            <Zap className="w-10 h-10 text-[#D4F34A]" />
          </div>
          <h1 className="font-heading text-3xl font-bold tracking-tight mb-4">
            Choose your plan
          </h1>
          <p className="text-text-muted text-lg mb-8">
            Based on how many Shopify stores you manage. All plans include a 7-day free trial.
          </p>
          
          {/* Tiered Pricing Cards */}
          <div className="grid md:grid-cols-2 gap-4 mb-8">
            {/* Operator Plan */}
            <Card className="bg-surface border-[#262626]">
              <CardContent className="pt-6">
                <h3 className="font-heading text-lg font-bold mb-1">Operator</h3>
                <p className="text-text-muted text-xs mb-4">For serious operators</p>
                <div className="flex items-baseline justify-center gap-1 mb-4">
                  <span className="font-heading text-3xl font-extrabold">$149</span>
                  <span className="text-text-muted text-sm">/mo</span>
                </div>
                <ul className="text-left space-y-2 mb-4 text-sm">
                  <li className="flex items-center gap-2 text-text-muted">
                    <CheckCircle2 className="w-3 h-3 text-[#D4F34A]" />
                    1 Shopify store
                  </li>
                  <li className="flex items-center gap-2 text-text-muted">
                    <CheckCircle2 className="w-3 h-3 text-[#D4F34A]" />
                    Weekly AI analysis
                  </li>
                  <li className="flex items-center gap-2 text-text-muted">
                    <CheckCircle2 className="w-3 h-3 text-[#D4F34A]" />
                    Email reports
                  </li>
                </ul>
                <Button
                  data-testid="subscribe-operator-btn"
                  onClick={() => handleStartCheckout("operator")}
                  disabled={creatingCheckout}
                  className="w-full bg-white/10 text-white hover:bg-white/20 font-semibold py-4"
                >
                  {creatingCheckout ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      Opening secure checkout...
                    </>
                  ) : "Start Trial"}
                </Button>
                <p className="text-xs text-gray-500 text-center mt-2">
                  Subscriptions managed through Shopify. Cancel anytime in Shopify Admin.
                </p>
              </CardContent>
            </Card>

            {/* Growth Plan */}
            <Card className="bg-surface border border-white/10 relative">
              <CardContent className="pt-6">
                <h3 className="font-heading text-lg font-bold mb-1">Growth</h3>
                <p className="text-text-muted text-xs mb-4">For operators running multiple stores</p>
                <div className="flex items-baseline justify-center gap-1 mb-4">
                  <span className="font-heading text-3xl font-extrabold">$299</span>
                  <span className="text-text-muted text-sm">/mo</span>
                </div>
                <ul className="text-left space-y-2 mb-4 text-sm">
                  <li className="flex items-center gap-2 text-white font-medium">
                    <CheckCircle2 className="w-3 h-3 text-[#D4F34A]" />
                    Up to 5 stores
                  </li>
                  <li className="flex items-center gap-2 text-text-muted">
                    <CheckCircle2 className="w-3 h-3 text-[#D4F34A]" />
                    Consolidated weekly digest
                  </li>
                  <li className="flex items-center gap-2 text-text-muted">
                    <CheckCircle2 className="w-3 h-3 text-[#D4F34A]" />
                    Per-store email reports
                  </li>
                </ul>
                <Button
                  data-testid="subscribe-growth-btn"
                  onClick={() => handleStartCheckout("growth")}
                  disabled={creatingCheckout}
                  className="w-full bg-white/10 text-white hover:bg-white/20 font-semibold py-4"
                >
                  {creatingCheckout ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      Opening secure checkout...
                    </>
                  ) : "Start Trial"}
                </Button>
                <p className="text-xs text-gray-500 text-center mt-2">
                  Subscriptions managed through Shopify. Cancel anytime in Shopify Admin.
                </p>
              </CardContent>
            </Card>
          </div>
          
          <p className="text-xs text-text-muted text-center mt-4">
            All plans include 7-day free trial. Cancel anytime. Questions? Contact hello@replydriven.com
          </p>
        </div>
      );
    }

    // Status: trialing - User is in trial period (show dashboard but with trial banner)
    // This is handled below - trialing users see the full dashboard

    // Status: incomplete - No longer used, redirect to subscribe
    if (subscriptionStatus === "incomplete") {
      return (
        <div className="max-w-lg text-center">
          <div className="w-20 h-20 rounded-full bg-[#FFA500]/10 flex items-center justify-center mx-auto mb-6">
            <AlertCircle className="w-10 h-10 text-[#FFA500]" />
          </div>
          <h1 className="font-heading text-3xl font-bold tracking-tight mb-4">
            Complete your subscription
          </h1>
          <p className="text-text-muted text-lg mb-8">
            Continue to complete your subscription and unlock all features.
          </p>
          
          <Card className="bg-surface border-[#262626] mb-8">
            <CardContent className="pt-6">
              <Button
                data-testid="continue-checkout-btn"
                onClick={handleStartCheckout}
                disabled={creatingCheckout}
                className="w-full bg-[#D4F34A] text-black hover:bg-[#D4F34A]/90 font-bold py-6 text-lg"
              >
                {creatingCheckout ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>Subscribe Now <CreditCard className="w-5 h-5 ml-2" /></>
                )}
              </Button>
            </CardContent>
          </Card>
        </div>
      );
    }

    // Status: past_due - Payment failed
    if (subscriptionStatus === "past_due") {
      return (
        <div className="max-w-lg text-center">
          <div className="w-20 h-20 rounded-full bg-[#FF453A]/10 flex items-center justify-center mx-auto mb-6">
            <AlertTriangle className="w-10 h-10 text-[#FF453A]" />
          </div>
          <h1 className="font-heading text-3xl font-bold tracking-tight mb-4">
            Payment issue
          </h1>
          <p className="text-text-muted text-lg mb-8">
            Your last payment failed. Please update your billing information to continue receiving insights.
          </p>
          
          <Card className="bg-surface border-[#262626] mb-8">
            <CardContent className="pt-6">
              <Button
                data-testid="update-billing-btn"
                onClick={handleOpenPortal}
                disabled={creatingPortal}
                className="w-full bg-[#D4F34A] text-black hover:bg-[#D4F34A]/90 font-bold py-6 text-lg"
              >
                {creatingPortal ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>Update Billing <CreditCard className="w-5 h-5 ml-2" /></>
                )}
              </Button>
            </CardContent>
          </Card>
        </div>
      );
    }

    // Status: cancelled - Subscription ended
    if (subscriptionStatus === "cancelled" || subscriptionStatus === "canceled") {
      return (
        <div className="max-w-4xl text-center">
          <div className="w-20 h-20 rounded-full bg-[#262626] flex items-center justify-center mx-auto mb-6">
            <Lock className="w-10 h-10 text-text-muted" />
          </div>
          <h1 className="font-heading text-3xl font-bold tracking-tight mb-4">
            Your subscription ended
          </h1>
          <p className="text-text-muted text-lg mb-8">
            Resubscribe to continue receiving weekly profit insights. Choose a plan based on how many stores you manage.
          </p>
          
          {/* Tiered Pricing Cards for Resubscribe */}
          <div className="grid md:grid-cols-2 gap-4 mb-8">
            {/* Operator Plan */}
            <Card className="bg-surface border-[#262626]">
              <CardContent className="pt-6">
                <h3 className="font-heading text-lg font-bold mb-1">Operator</h3>
                <p className="text-text-muted text-xs mb-4">For serious operators</p>
                <div className="flex items-baseline justify-center gap-1 mb-4">
                  <span className="font-heading text-3xl font-extrabold">$149</span>
                  <span className="text-text-muted text-sm">/mo</span>
                </div>
                <ul className="text-left space-y-2 mb-4 text-sm">
                  <li className="flex items-center gap-2 text-text-muted">
                    <CheckCircle2 className="w-3 h-3 text-[#D4F34A]" />
                    1 Shopify store
                  </li>
                  <li className="flex items-center gap-2 text-text-muted">
                    <CheckCircle2 className="w-3 h-3 text-[#D4F34A]" />
                    Weekly AI analysis
                  </li>
                </ul>
                <Button
                  data-testid="resubscribe-operator-btn"
                  onClick={() => handleStartCheckout("operator")}
                  disabled={creatingCheckout}
                  className="w-full bg-white/10 text-white hover:bg-white/20 font-semibold py-4"
                >
                  {creatingCheckout ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      Opening secure checkout...
                    </>
                  ) : "Resubscribe"}
                </Button>
                <p className="text-xs text-gray-500 text-center mt-2">
                  Subscriptions managed through Shopify.
                </p>
              </CardContent>
            </Card>

            {/* Growth Plan */}
            <Card className="bg-surface border border-white/10 relative">
              <CardContent className="pt-6">
                <h3 className="font-heading text-lg font-bold mb-1">Growth</h3>
                <p className="text-text-muted text-xs mb-4">For operators running multiple stores</p>
                <div className="flex items-baseline justify-center gap-1 mb-4">
                  <span className="font-heading text-3xl font-extrabold">$299</span>
                  <span className="text-text-muted text-sm">/mo</span>
                </div>
                <ul className="text-left space-y-2 mb-4 text-sm">
                  <li className="flex items-center gap-2 text-white font-medium">
                    <CheckCircle2 className="w-3 h-3 text-[#D4F34A]" />
                    Up to 5 stores
                  </li>
                  <li className="flex items-center gap-2 text-text-muted">
                    <CheckCircle2 className="w-3 h-3 text-[#D4F34A]" />
                    Consolidated weekly digest
                  </li>
                </ul>
                <Button
                  data-testid="resubscribe-growth-btn"
                  onClick={() => handleStartCheckout("growth")}
                  disabled={creatingCheckout}
                  className="w-full bg-white/10 text-white hover:bg-white/20 font-semibold py-4"
                >
                  {creatingCheckout ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      Opening secure checkout...
                    </>
                  ) : "Resubscribe"}
                </Button>
                <p className="text-xs text-gray-500 text-center mt-2">
                  Subscriptions managed through Shopify.
                </p>
              </CardContent>
            </Card>
          </div>
          
          <p className="text-xs text-text-muted text-center mt-4 mb-2">
            All plans include 7-day free trial. Questions? Contact hello@replydriven.com
          </p>
          <button 
            onClick={handleResetBilling}
            className="text-xs text-text-muted underline hover:text-white transition-colors"
          >
            Having trouble? Reset billing state
          </button>
        </div>
      );
    }

    return null;
  };

  // Show backend error banner instead of paywall when there's a network/server error
  // Only show paywall when backend returns a real "inactive" response (200 OK with inactive status)
  if (backendError && !hasActiveSubscription) {
    return (
      <div className="min-h-screen bg-[#050505] text-white">
        <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-black/50 border-b border-white/5">
          <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
            <Logo showText linkTo="/" />
            <Button variant="ghost" size="icon" className="text-text-muted hover:text-white" onClick={logout} data-testid="logout-btn">
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </nav>

        <main className="pt-24 pb-12 px-6 md:px-12 flex items-center justify-center min-h-screen">
          <div className="max-w-lg text-center">
            <div className="w-20 h-20 rounded-full bg-yellow-500/10 flex items-center justify-center mx-auto mb-6">
              <AlertCircle className="w-10 h-10 text-yellow-500" />
            </div>
            <h1 className="font-heading text-2xl font-bold tracking-tight mb-4">
              Backend temporarily unavailable
            </h1>
            <p className="text-text-muted mb-6">
              We could not connect to our servers. This is usually temporary.
            </p>
            <Button
              onClick={retryBackend}
              disabled={loading}
              className="bg-[#D4F34A] text-black hover:bg-[#D4F34A]/90 font-semibold px-8 py-4"
              data-testid="retry-backend-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Retrying...
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Retry
                </>
              )}
            </Button>
            <p className="text-xs text-text-muted mt-4">
              Error: {backendError.status} - {backendError.message}
            </p>
          </div>
        </main>
      </div>
    );
  }

  // Show paywall ONLY if billingStatus was successfully fetched and shows inactive
  // This prevents showing paywall on network errors
  if (!hasActiveSubscription && billingStatus !== null) {
    return (
      <div className="min-h-screen bg-[#050505] text-white">
        <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-black/50 border-b border-white/5">
          <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
            <Logo showText linkTo="/" />
            <Button variant="ghost" size="icon" className="text-text-muted hover:text-white" onClick={logout} data-testid="logout-btn">
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </nav>

        <main className="pt-24 pb-12 px-6 md:px-12 flex items-center justify-center min-h-screen">
          {renderPaywall()}
        </main>
        
        {/* Billing Pending Modal - also needed in paywall view */}
        {showBillingPending && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Backdrop */}
            <div 
              className="absolute inset-0 bg-black/80 backdrop-blur-sm"
              onClick={() => !notifyRegistered && setShowBillingPending(false)}
            />
            
            {/* Modal */}
            <div className="relative bg-surface border border-[#262626] rounded-2xl p-8 max-w-md w-full text-center shadow-2xl" data-testid="billing-pending-modal">
              {notifyRegistered ? (
                // Confirmation state
                <>
                  <div className="w-16 h-16 rounded-full bg-[#D4F34A]/10 flex items-center justify-center mx-auto mb-5">
                    <CheckCircle2 className="w-8 h-8 text-[#D4F34A]" />
                  </div>
                  <h2 className="font-heading text-2xl font-bold mb-3">
                    You&apos;re on the list
                  </h2>
                  <p className="text-text-muted mb-6">
                    We&apos;ll email you as soon as billing opens.
                  </p>
                  <p className="text-sm text-text-muted mb-6">
                    Your dashboard is ready. You can explore while waiting for billing to go live.
                  </p>
                  <Button
                    onClick={() => setShowBillingPending(false)}
                    variant="outline"
                    className="border-[#262626] text-white hover:bg-[#262626]"
                  >
                    Got it
                  </Button>
                </>
              ) : (
                // Initial state
                <>
                  <div className="w-16 h-16 rounded-full bg-[#D4F34A]/10 flex items-center justify-center mx-auto mb-5">
                    <Clock className="w-8 h-8 text-[#D4F34A]" />
                  </div>
                  <h2 className="font-heading text-2xl font-bold mb-3">
                    Billing is opening shortly
                  </h2>
                  <p className="text-text-muted mb-6">
                    We&apos;re finishing payment activation and will notify you the moment billing opens.
                  </p>
                  
                  <Button
                    onClick={handleNotifyMe}
                    disabled={registeringIntent}
                    className="bg-[#D4F34A] text-black hover:bg-[#D4F34A]/90 font-bold py-5 px-8 text-base w-full"
                    data-testid="notify-me-btn"
                  >
                    {registeringIntent ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        Registering...
                      </>
                    ) : (
                      <>
                        <Bell className="w-4 h-4 mr-2" />
                        Notify me when billing opens
                      </>
                    )}
                  </Button>
                  
                  <button
                    onClick={() => setShowBillingPending(false)}
                    className="mt-4 text-sm text-text-muted hover:text-white transition-colors"
                  >
                    Close
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  // Active or trialing subscription - show full dashboard
  return (
    <div className="min-h-screen bg-[#050505] text-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-black/50 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
          <Logo showText linkTo="/" />
          
          <div className="flex items-center gap-4">
            <span className="text-text-muted text-sm hidden md:block">{currentUser?.email}</span>
            {/* Affiliate Dashboard Link - visible to affiliates */}
            {isAffiliate && (
              <Link to="/affiliates/dashboard">
                <Button variant="ghost" className="text-text-muted hover:text-white text-sm" data-testid="affiliate-dashboard-btn">
                  <Gift className="w-4 h-4 mr-1.5" />
                  <span className="hidden sm:inline">Affiliate Dashboard</span>
                  <span className="sm:hidden">Affiliate</span>
                </Button>
              </Link>
            )}
            {/* Admin Link - only visible to admin users */}
            {isAdmin && (
              <Link to={payoutRequestsCount > 0 ? "/admin/payouts" : "/admin"}>
                <Button variant="ghost" className="text-text-muted hover:text-white text-sm relative" data-testid="admin-btn">
                  <Shield className="w-4 h-4 mr-1.5" />
                  Admin
                  {adminBadgeCount > 0 && (
                    <span className={`absolute -top-1 -right-1 text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center ${
                      payoutRequestsCount > 0 ? "bg-green-500 text-white" : "bg-[#D4F34A] text-black"
                    }`}>
                      {adminBadgeCount > 9 ? "9+" : adminBadgeCount}
                    </span>
                  )}
                </Button>
              </Link>
            )}
            <Link to="/settings">
              <Button variant="ghost" size="icon" className="text-text-muted hover:text-white" data-testid="settings-btn">
                <Settings className="w-5 h-5" />
              </Button>
            </Link>
            <Button variant="ghost" size="icon" className="text-text-muted hover:text-white" onClick={logout} data-testid="logout-btn">
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </nav>

      <main className="pt-24 pb-12 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          {/* Trial Banner - Non-blocking info banner for trial users */}
          {isTrialing && trialDaysRemaining !== null && (
            <div className="bg-[#1a1a1a] border border-[#262626] rounded-lg px-4 py-3 mb-6">
              <div className="flex items-center gap-3">
                <Clock className="w-4 h-4 text-text-muted flex-shrink-0" />
                <p className="text-sm text-text-muted">
                  {trialDaysRemaining > 0 
                    ? <><span className="text-white font-medium">{trialDaysRemaining} day{trialDaysRemaining === 1 ? '' : 's'}</span> left in your free trial</>
                    : <span className="text-white font-medium">Your trial ends today</span>}
                </p>
              </div>
            </div>
          )}

          {/* OAuth Error Recovery Banner */}
          {oauthError && (
            <Card className="bg-gradient-to-r from-[#FF453A]/10 to-transparent border-[#FF453A]/30 mb-6" data-testid="oauth-error-banner">
              <CardContent className="py-4">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#FF453A]/10 flex items-center justify-center flex-shrink-0">
                      <AlertTriangle className="w-5 h-5 text-[#FF453A]" />
                    </div>
                    <div>
                      <p className="font-semibold text-white">{oauthError.title}</p>
                      <p className="text-sm text-text-muted">{oauthError.message}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {oauthError.recoverable && (
                      <Link to="/onboarding">
                        <Button 
                          className="bg-[#D4F34A] text-black hover:bg-[#D4F34A]/90 font-bold whitespace-nowrap"
                          data-testid="retry-connect-btn"
                        >
                          Retry Connect
                        </Button>
                      </Link>
                    )}
                    <Button 
                      variant="ghost" 
                      size="icon"
                      className="text-text-muted hover:text-white"
                      onClick={() => setOauthError(null)}
                      data-testid="dismiss-oauth-error-btn"
                    >
                      <XCircle className="w-5 h-5" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Cancellation Warning Banner */}
          {billingStatus?.cancel_at_period_end && (
            <Card className="bg-gradient-to-r from-[#FF453A]/10 to-transparent border-[#FF453A]/20 mb-6">
              <CardContent className="py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <AlertTriangle className="w-5 h-5 text-[#FF453A]" />
                    <div>
                      <p className="font-semibold text-white">Your subscription will end {billingStatus.current_period_end ? `on ${formatDate(billingStatus.current_period_end)}` : 'soon'}</p>
                      <p className="text-sm text-text-muted">After this date, you will lose access to insights and Shopify sync.</p>
                    </div>
                  </div>
                  <Button 
                    onClick={handleOpenPortal}
                    disabled={creatingPortal}
                    className="bg-[#D4F34A] text-black hover:bg-[#D4F34A]/90 font-bold"
                  >
                    {creatingPortal ? <Loader2 className="w-4 h-4 animate-spin" /> : "Manage Subscription"}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Getting Started Checklist */}
          <GettingStartedChecklist
            hasStore={stores.length > 0}
            hasAnalysis={!!latestReport}
            hasActiveSubscription={hasActiveSubscription}
            onConnectStore={() => {
              // Scroll to store connection section or show modal
              if (healthStatus?.shopify_configured) {
                setShowAddStore(true);
              } else {
                toast.info("Shopify integration is being set up. Please check back soon!");
              }
            }}
            onStartCheckout={() => handleStartCheckout("operator")}
          />

          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight mb-2">
                  Welcome back, {currentUser?.name?.split(' ')[0]}
                </h1>
                <p className="text-text-muted">
                  {store ? "Here's your store's latest performance insight" : "Connect your Shopify store to get started"}
                </p>
              </div>
              
              {/* Store Selector + Plan Badge */}
              <div className="flex items-center gap-3 flex-wrap">
                {/* Store Selector - Only show for multi-store plans or when multiple stores exist */}
                {(stores.length > 1 || storeLimit > 1) && (
                  <StoreSelector
                    stores={stores}
                    activeStoreId={activeStoreId}
                    onStoreChange={handleStoreChange}
                    storeLimit={storeLimit}
                    onAddStore={() => setShowAddStore(true)}
                  />
                )}
                
                
                {/* Plan Badge */}
                <div className="bg-surface border border-[#262626] rounded-lg px-4 py-2">
                  <p className="text-xs text-text-muted">Current Plan</p>
                  <p className="font-semibold text-white">{planName}</p>
                  <p className="text-xs text-text-muted">
                    {connectedStoreCount}/{storeLimit} store{storeLimit > 1 ? 's' : ''}
                  </p>
                </div>
                {currentPlan !== "growth" && (
                  <AlertDialog open={showUpgradeModal} onOpenChange={setShowUpgradeModal}>
                    <AlertDialogTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        className="border-[#D4F34A] text-[#D4F34A] hover:bg-[#D4F34A]/10"
                        disabled={creatingCheckout}
                      >
                        {creatingCheckout ? <Loader2 className="w-4 h-4 animate-spin" /> : "Upgrade"}
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="bg-surface border-[#262626] max-w-md">
                      <AlertDialogHeader>
                        <AlertDialogTitle className="text-white text-xl">Upgrade to Growth</AlertDialogTitle>
                        <AlertDialogDescription className="text-text-muted">
                          Monitor up to 5 Shopify stores with a consolidated weekly digest.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <div className="space-y-3 py-4">
                        <div 
                          className="p-4 border border-[#262626] rounded-lg hover:border-[#D4F34A]/50 cursor-pointer transition-colors"
                          onClick={() => handleUpgrade("growth")}
                        >
                          <div className="flex justify-between items-center mb-2">
                            <span className="font-semibold text-white">Growth</span>
                            <span className="text-[#D4F34A] font-bold">$299/mo</span>
                          </div>
                          <p className="text-sm text-text-muted">Up to 5 Shopify stores</p>
                          <ul className="text-xs text-text-muted mt-2 space-y-1">
                            <li>• Weekly profit insight per store</li>
                            <li>• Consolidated weekly digest</li>
                            <li>• Per-store email reports</li>
                          </ul>
                        </div>
                        <p className="text-xs text-text-muted mt-4 text-center">
                          You&apos;ll be redirected to the billing portal to manage your plan change.
                        </p>
                      </div>
                      <AlertDialogFooter>
                        <AlertDialogCancel className="bg-transparent border-[#262626] text-white hover:bg-[#262626]">
                          Cancel
                        </AlertDialogCancel>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
              </div>
            </div>
          </div>

          {/* Shopify Not Configured Banner */}
          {healthStatus && !healthStatus.shopify_configured && (
            <Card className="bg-[#262626]/50 border-[#363636] mb-8">
              <CardContent className="py-4">
                <div className="flex items-center gap-3">
                  <Info className="w-5 h-5 text-text-muted" />
                  <div>
                    <p className="font-semibold text-white">Shopify setup in progress</p>
                    <p className="text-sm text-text-muted">
                      Store connection is being configured. Please try again later.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Main Content */}
          {!store ? (
            /* No Store Connected - Show Connect Flow */
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Connect Store Card */}
              <Card className="bg-surface border-[#262626]">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Store className="w-5 h-5 text-[#D4F34A]" />
                    Connect Your Shopify Store
                  </CardTitle>
                  <CardDescription>
                    Enter your Shopify store domain to start receiving weekly profit insights.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex gap-2">
                      <Input
                        data-testid="shop-domain-input"
                        placeholder="yourstore.myshopify.com"
                        value={shopDomain}
                        onChange={(e) => setShopDomain(e.target.value)}
                        className="bg-[#1E1E1E] border-[#262626] text-white placeholder:text-text-muted"
                        disabled={!healthStatus?.shopify_configured}
                      />
                      <Button
                        data-testid="connect-store-btn"
                        onClick={handleConnectStore}
                        disabled={connecting || !healthStatus?.shopify_configured}
                        className="bg-[#D4F34A] text-black hover:bg-[#D4F34A]/90 font-bold whitespace-nowrap"
                      >
                        {connecting ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>Connect <ExternalLink className="w-4 h-4 ml-2" /></>
                        )}
                      </Button>
                    </div>
                    <p className="text-xs text-text-muted">
                      Read-only access. We never modify your store data.
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Setup Instructions (Admin Only) */}
              {showSetupInstructions && healthStatus && !healthStatus.shopify_configured && (
                <Card className="bg-surface border-[#262626]">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-[#FFA500]">
                      <AlertTriangle className="w-5 h-5" />
                      Admin: Setup Required
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3 text-sm">
                      <p className="text-text-muted">To enable Shopify connection, set these environment variables:</p>
                      <ul className="space-y-2 text-text-muted">
                        <li className="flex items-center gap-2">
                          {healthStatus.shopify_configured ? (
                            <CheckCircle2 className="w-4 h-4 text-[#D4F34A]" />
                          ) : (
                            <XCircle className="w-4 h-4 text-[#FF453A]" />
                          )}
                          <code className="text-xs bg-[#1E1E1E] px-2 py-1 rounded">SHOPIFY_API_KEY</code>
                        </li>
                        <li className="flex items-center gap-2">
                          {healthStatus.shopify_configured ? (
                            <CheckCircle2 className="w-4 h-4 text-[#D4F34A]" />
                          ) : (
                            <XCircle className="w-4 h-4 text-[#FF453A]" />
                          )}
                          <code className="text-xs bg-[#1E1E1E] px-2 py-1 rounded">SHOPIFY_API_SECRET</code>
                        </li>
                      </ul>
                    </div>
                  </CardContent>
                </Card>
              )}

              {!showSetupInstructions && healthStatus?.shopify_configured && (
                <Card className="bg-surface border-[#262626]">
                  <CardContent className="pt-6">
                    <div className="text-center py-8">
                      <div className="w-16 h-16 rounded-full bg-[#D4F34A]/10 flex items-center justify-center mx-auto mb-4">
                        <CheckCircle2 className="w-8 h-8 text-[#D4F34A]" />
                      </div>
                      <h3 className="font-heading font-semibold text-lg mb-2">Ready to Connect</h3>
                      <p className="text-text-muted text-sm max-w-sm mx-auto">
                        Enter your Shopify store domain to start receiving weekly profit leak insights.
                      </p>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          ) : (
            /* Store Connected - Show Dashboard */
            <div className="space-y-8">
              {/* Connected Stores Section */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Render each connected store */}
                {stores.map((storeItem) => (
                  <Card key={storeItem.store_id} className="bg-surface border-[#262626]">
                    <CardContent className="pt-6 pb-4">
                      <div className="flex items-start justify-between mb-4">
                        <div className="w-10 h-10 rounded-lg bg-[#D4F34A]/10 flex items-center justify-center flex-shrink-0">
                          <Store className="w-5 h-5 text-[#D4F34A]" />
                        </div>
                        <span className="text-xs bg-[#D4F34A]/20 text-[#D4F34A] px-2 py-1 rounded-full flex items-center gap-1">
                          <CheckCircle2 className="w-3 h-3" /> Connected
                        </span>
                      </div>
                      <p className="font-heading font-semibold text-lg mb-1 break-all">{storeItem.shop_domain}</p>
                      <p className="text-xs text-text-muted flex items-center gap-1 mb-4">
                        <Clock className="w-3 h-3" />
                        Last sync: {storeItem.last_sync ? formatDate(storeItem.last_sync) : "Never"}
                      </p>
                      
                      {/* Disconnect Button with Confirmation - properly aligned inside card */}
                      <div className="pt-3 border-t border-[#262626]">
                        <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className="text-text-muted hover:text-white hover:bg-[#262626] w-full justify-start"
                            disabled={disconnecting}
                          >
                            {disconnecting ? (
                              <Loader2 className="w-3 h-3 mr-2 animate-spin" />
                            ) : (
                              <Unplug className="w-3 h-3 mr-2" />
                            )}
                            Disconnect Store
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent className="bg-surface border-[#262626]">
                          <AlertDialogHeader>
                            <AlertDialogTitle className="text-white">Disconnect {storeItem.shop_domain}?</AlertDialogTitle>
                            <AlertDialogDescription className="text-text-muted">
                              Disconnecting will stop data syncing and remove your stored access token. 
                              Your subscription will remain active, and you can reconnect anytime.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel className="bg-transparent border-[#262626] text-white hover:bg-[#262626]">
                              Cancel
                            </AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => handleDisconnectStore(storeItem.store_id)}
                              className="bg-[#FF453A] text-white hover:bg-[#FF453A]/90"
                            >
                              Disconnect
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                      </div>
                    </CardContent>
                  </Card>
                ))}

                {/* Add Another Store Card - Show if user can add more stores */}
                {canAddStore && stores.length < storeLimit && (
                  <Card className="bg-surface border-[#262626] border-dashed">
                    <CardContent className="pt-6">
                      <div className="text-center py-4">
                        <div className="w-10 h-10 rounded-lg bg-[#262626] flex items-center justify-center mx-auto mb-4">
                          <Plus className="w-5 h-5 text-text-muted" />
                        </div>
                        <p className="font-heading font-semibold text-lg mb-1 text-text-muted">Add Another Store</p>
                        <p className="text-xs text-text-muted mb-4">
                          {stores.length}/{storeLimit} stores connected
                        </p>
                        
                        {showAddStore ? (
                          <div className="space-y-3">
                            <Input
                              data-testid="add-store-domain-input"
                              placeholder="yourstore.myshopify.com"
                              value={shopDomain}
                              onChange={(e) => setShopDomain(e.target.value)}
                              className="bg-[#1E1E1E] border-[#262626] text-white placeholder:text-text-muted"
                              disabled={!healthStatus?.shopify_configured}
                            />
                            <div className="flex gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setShowAddStore(false);
                                  setShopDomain("");
                                }}
                                className="flex-1 border-[#262626] text-text-muted hover:bg-[#262626]"
                              >
                                Cancel
                              </Button>
                              <Button
                                data-testid="connect-additional-store-btn"
                                size="sm"
                                onClick={handleConnectStore}
                                disabled={connecting || !healthStatus?.shopify_configured}
                                className="flex-1 bg-[#D4F34A] text-black hover:bg-[#D4F34A]/90 font-bold"
                              >
                                {connecting ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <>Connect</>
                                )}
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <Button
                            data-testid="add-store-btn"
                            variant="outline"
                            size="sm"
                            onClick={() => setShowAddStore(true)}
                            disabled={!healthStatus?.shopify_configured}
                            className="border-[#D4F34A] text-[#D4F34A] hover:bg-[#D4F34A]/10"
                          >
                            <Plus className="w-4 h-4 mr-2" />
                            Connect Store
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {latestReport && (
                  <Card className="lg:col-span-2 bg-gradient-to-br from-[#FF453A]/10 to-transparent border-[#FF453A]/20 relative overflow-hidden" data-testid="profit-leak-card">
                    <div className="absolute top-3 right-3 bg-[#FF453A] text-white text-xs font-bold px-3 py-1 rounded-full">
                      PROFIT LEAK
                    </div>
                    <CardContent className="pt-6">
                      <div className="flex items-start gap-4 mb-4">
                        <div className="w-12 h-12 rounded-xl bg-[#FF453A]/20 flex items-center justify-center flex-shrink-0">
                          <AlertTriangle className="w-6 h-6 text-[#FF453A]" />
                        </div>
                        <div>
                          <p className="text-text-muted text-sm mb-1">Estimated Revenue Impact</p>
                          <p className="font-heading text-3xl font-extrabold text-[#FF453A]">
                            ~{formatCurrency(latestReport.biggest_profit_leak?.estimated_revenue_impact_usd || 0)}
                          </p>
                        </div>
                      </div>
                      <div className="space-y-3">
                        <div>
                          <p className="text-xs text-text-muted uppercase tracking-wider mb-1">Issue</p>
                          <p className="font-semibold">{latestReport.biggest_profit_leak?.title}</p>
                        </div>
                        <div>
                          <p className="text-xs text-text-muted uppercase tracking-wider mb-1">What Changed</p>
                          <p className="text-text-muted text-sm">{latestReport.biggest_profit_leak?.what_changed}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {!latestReport && (
                  <Card className="lg:col-span-2 bg-surface border-[#262626] border-dashed">
                    <CardContent className="pt-6 text-center py-12">
                      <BarChart3 className="w-12 h-12 text-text-muted mx-auto mb-4" />
                      <p className="text-text-muted mb-4">No analysis yet</p>
                      <Button
                        data-testid="run-first-analysis-btn"
                        onClick={handleRunAnalysis}
                        disabled={analyzing}
                        className="bg-[#D4F34A] text-black hover:bg-[#D4F34A]/90 font-bold"
                      >
                        {analyzing ? (
                          <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analyzing...</>
                        ) : (
                          <>Run First Analysis</>
                        )}
                      </Button>
                    </CardContent>
                  </Card>
                )}
              </div>

              {latestReport && (
                <>
                  {/* Recommended Action */}
                  <Card className="bg-gradient-to-br from-[#D4F34A]/10 to-transparent border-[#D4F34A]/20">
                    <CardContent className="pt-6">
                      <div className="flex items-center gap-2 text-[#D4F34A] mb-3">
                        <CheckCircle2 className="w-5 h-5" />
                        <span className="text-sm font-medium uppercase tracking-wider">Recommended Action</span>
                      </div>
                      <p className="text-lg font-semibold">{latestReport.biggest_profit_leak?.recommended_action}</p>
                    </CardContent>
                  </Card>

                  {/* Metrics Grid */}
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <MetricCard
                      icon={<DollarSign className="w-5 h-5" />}
                      label="Revenue"
                      value={formatCurrency(latestReport.current_metrics?.revenue || 0)}
                      change={latestReport.previous_metrics?.revenue ? 
                        ((latestReport.current_metrics?.revenue / latestReport.previous_metrics?.revenue - 1) * 100).toFixed(1) : null}
                    />
                    <MetricCard
                      icon={<ShoppingCart className="w-5 h-5" />}
                      label="Orders"
                      value={latestReport.current_metrics?.orders_count || 0}
                      change={latestReport.previous_metrics?.orders_count ? 
                        ((latestReport.current_metrics?.orders_count / latestReport.previous_metrics?.orders_count - 1) * 100).toFixed(1) : null}
                    />
                    <MetricCard
                      icon={<DollarSign className="w-5 h-5" />}
                      label="AOV"
                      value={formatCurrency(latestReport.current_metrics?.aov || 0)}
                      change={latestReport.previous_metrics?.aov ? 
                        ((latestReport.current_metrics?.aov / latestReport.previous_metrics?.aov - 1) * 100).toFixed(1) : null}
                    />
                    <MetricCard
                      icon={<Users className="w-5 h-5" />}
                      label="Returning Rate"
                      value={`${latestReport.current_metrics?.returning_rate || 0}%`}
                      change={latestReport.previous_metrics?.returning_rate ? 
                        (latestReport.current_metrics?.returning_rate - latestReport.previous_metrics?.returning_rate).toFixed(1) : null}
                      isPercentagePoint
                    />
                  </div>

                  {/* Affiliate CTA - Show only after user has seen insights */}
                  {!isAffiliate && !affiliateCtaDismissed && (
                    <Card className="bg-gradient-to-r from-[#1a2420] to-[#121816] border-[#2a3a30]" data-testid="affiliate-cta-card">
                      <CardContent className="py-5">
                        <div className="flex items-center justify-between gap-4">
                          <div className="flex items-center gap-4">
                            <div className="w-10 h-10 rounded-full bg-[#D4F34A]/10 flex items-center justify-center flex-shrink-0">
                              <Gift className="w-5 h-5 text-[#D4F34A]" />
                            </div>
                            <div>
                              <p className="font-semibold text-white">Know other Shopify stores?</p>
                              <p className="text-sm text-text-muted">Earn 30% recurring for every store you refer.</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Link to="/affiliates">
                              <Button 
                                size="sm" 
                                className="bg-[#D4F34A] text-black hover:bg-[#D4F34A]/90 font-bold whitespace-nowrap"
                                data-testid="become-affiliate-btn"
                              >
                                Become an affiliate
                              </Button>
                            </Link>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={dismissAffiliateCta}
                              className="text-text-muted hover:text-white h-8 w-8"
                              data-testid="dismiss-affiliate-cta"
                            >
                              <XCircle className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* What Worked / Ignore */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <Card className="bg-surface border-[#262626]">
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-lg">
                          <TrendingUp className="w-5 h-5 text-[#D4F34A]" />
                          What Worked
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-text-muted">{latestReport.what_worked}</p>
                      </CardContent>
                    </Card>
                    
                    <Card className="bg-surface border-[#262626]">
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-lg">
                          <XCircle className="w-5 h-5 text-text-muted" />
                          What to Ignore
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-text-muted">{latestReport.what_to_ignore}</p>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Top Products */}
                  {latestReport.current_metrics?.top_products?.length > 0 && (
                    <Card className="bg-surface border-[#262626]">
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-lg">
                          <Package className="w-5 h-5 text-[#D4F34A]" />
                          Top Products by Revenue
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3">
                          {latestReport.current_metrics.top_products.map((product, index) => (
                            <div key={product.product_id || index} className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <span className="text-text-muted text-sm w-6">#{index + 1}</span>
                                <span className="truncate max-w-xs">{product.title}</span>
                              </div>
                              <span className="font-heading font-semibold text-[#D4F34A]">
                                {formatCurrency(product.revenue)}
                              </span>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Analysis History */}
                  {reportHistory.length > 0 && (
                    <Card className="bg-surface border-[#262626]">
                      <CardHeader className="flex flex-row items-center justify-between">
                        <CardTitle className="text-lg">Analysis History</CardTitle>
                        <Button
                          data-testid="run-analysis-btn"
                          variant="outline"
                          size="sm"
                          onClick={handleRunAnalysis}
                          disabled={analyzing}
                          className="border-[#262626] hover:bg-[#1E1E1E]"
                        >
                          {analyzing ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <><RefreshCw className="w-4 h-4 mr-2" /> Run Now</>
                          )}
                        </Button>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3">
                          {reportHistory.map((report) => (
                            <div 
                              key={report.report_id} 
                              className="flex items-center justify-between py-3 border-b border-[#262626] last:border-0"
                            >
                              <div className="flex items-center gap-4">
                                <div className="w-8 h-8 rounded-lg bg-[#FF453A]/10 flex items-center justify-center">
                                  <AlertTriangle className="w-4 h-4 text-[#FF453A]" />
                                </div>
                                <div>
                                  <p className="font-medium">{report.biggest_profit_leak?.title}</p>
                                  <p className="text-xs text-text-muted">{formatDate(report.created_at)}</p>
                                </div>
                              </div>
                              <span className="text-[#FF453A] font-heading font-bold">
                                ~{formatCurrency(report.biggest_profit_leak?.estimated_revenue_impact_usd || 0)}
                              </span>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </main>
      
      {/* Billing Pending Modal */}
      {showBillingPending && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/80 backdrop-blur-sm"
            onClick={() => !notifyRegistered && setShowBillingPending(false)}
          />
          
          {/* Modal */}
          <div className="relative bg-surface border border-[#262626] rounded-2xl p-8 max-w-md w-full text-center shadow-2xl" data-testid="billing-pending-modal">
            {notifyRegistered ? (
              // Confirmation state
              <>
                <div className="w-16 h-16 rounded-full bg-[#D4F34A]/10 flex items-center justify-center mx-auto mb-5">
                  <CheckCircle2 className="w-8 h-8 text-[#D4F34A]" />
                </div>
                <h2 className="font-heading text-2xl font-bold mb-3">
                  You&apos;re on the list
                </h2>
                <p className="text-text-muted mb-6">
                  We&apos;ll email you as soon as billing opens.
                </p>
                <p className="text-sm text-text-muted mb-6">
                  Your dashboard is ready. You can explore while waiting for billing to go live.
                </p>
                <Button
                  onClick={() => setShowBillingPending(false)}
                  variant="outline"
                  className="border-[#262626] text-white hover:bg-[#262626]"
                >
                  Got it
                </Button>
              </>
            ) : (
              // Initial state
              <>
                <div className="w-16 h-16 rounded-full bg-[#D4F34A]/10 flex items-center justify-center mx-auto mb-5">
                  <Clock className="w-8 h-8 text-[#D4F34A]" />
                </div>
                <h2 className="font-heading text-2xl font-bold mb-3">
                  Billing is opening shortly
                </h2>
                <p className="text-text-muted mb-6">
                  We&apos;re finishing payment activation and will notify you the moment billing opens.
                </p>
                
                <Button
                  onClick={handleNotifyMe}
                  disabled={registeringIntent}
                  className="bg-[#D4F34A] text-black hover:bg-[#D4F34A]/90 font-bold py-5 px-8 text-base w-full"
                  data-testid="notify-me-btn"
                >
                  {registeringIntent ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      Registering...
                    </>
                  ) : (
                    <>
                      <Bell className="w-4 h-4 mr-2" />
                      Notify me when billing opens
                    </>
                  )}
                </Button>
                
                <button
                  onClick={() => setShowBillingPending(false)}
                  className="mt-4 text-sm text-text-muted hover:text-white transition-colors"
                >
                  Close
                </button>
              </>
            )}
          </div>
        </div>
      )}
      
      {/* First Insight Affiliate Modal - High Intent Moment */}
      {showFirstInsightAffiliate && !isAffiliate && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4" data-testid="first-insight-affiliate-modal">
          {/* Backdrop - clicking dismisses */}
          <div 
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setShowFirstInsightAffiliate(false)}
          />
          
          {/* Modal - slides up on mobile */}
          <div className="relative bg-surface border border-[#262626] rounded-2xl p-6 max-w-md w-full shadow-2xl animate-in slide-in-from-bottom-4 sm:slide-in-from-bottom-0">
            <button
              onClick={() => setShowFirstInsightAffiliate(false)}
              className="absolute top-4 right-4 text-text-muted hover:text-white"
            >
              <XCircle className="w-5 h-5" />
            </button>
            
            <div className="text-center">
              <div className="w-14 h-14 rounded-full bg-[#D4F34A]/10 flex items-center justify-center mx-auto mb-4">
                <Gift className="w-7 h-7 text-[#D4F34A]" />
              </div>
              
              <h3 className="font-heading text-xl font-bold mb-2">
                You just saw your first ReplyDriven insight.
              </h3>
              
              <p className="text-text-muted mb-6">
                If you know other Shopify store owners, you can earn 30% recurring by referring them.
              </p>
              
              <Link to="/affiliates">
                <Button 
                  className="bg-[#D4F34A] text-black hover:bg-[#D4F34A]/90 font-bold py-5 px-8 w-full"
                  data-testid="get-affiliate-link-btn"
                >
                  Get affiliate link
                </Button>
              </Link>
              
              <button
                onClick={() => setShowFirstInsightAffiliate(false)}
                className="mt-3 text-sm text-text-muted hover:text-white transition-colors"
              >
                You can do this later from your dashboard.
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Metric Card Component
const MetricCard = ({ icon, label, value, change, isPercentagePoint = false }) => {
  const isPositive = parseFloat(change) > 0;
  const isNegative = parseFloat(change) < 0;

  return (
    <Card className="bg-surface border-[#262626]">
      <CardContent className="pt-6">
        <div className="flex items-center gap-2 text-text-muted mb-2">
          {icon}
          <span className="text-xs uppercase tracking-wider">{label}</span>
        </div>
        <p className="font-heading text-2xl font-bold mb-1">{value}</p>
        {change !== null && (
          <p className={`text-sm flex items-center gap-1 ${isPositive ? 'text-[#D4F34A]' : isNegative ? 'text-[#FF453A]' : 'text-text-muted'}`}>
            {isPositive ? <TrendingUp className="w-3 h-3" /> : isNegative ? <TrendingDown className="w-3 h-3" /> : null}
            {isPositive ? '+' : ''}{change}{isPercentagePoint ? 'pp' : '%'} vs last week
          </p>
        )}
      </CardContent>
    </Card>
  );
};

import { BarChart3 } from "lucide-react";

export default Dashboard;
